import { useState } from "react";
import { Alert, Box, Button, Paper, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/**
 * Renders the outcome of an invite/account-creation action, plus a
 * copy-to-clipboard button for the password-setup link. Shared across all
 * three admin panels (Katana, Chain, Pharmacy) so the messaging never
 * drifts between them.
 *
 * Invite emails are sent for real via Resend — the success copy reflects
 * that ("a password-setup email has been sent"). Only pass
 * `emailSent={false}` when the server reports the credentials email
 * genuinely failed to send; that renders a warning telling the admin the
 * recipient will NOT get an email and the link must be shared manually.
 *
 * Pass `link={null}` (or leave it falsy) for the "existing account, no new
 * link was generated" case — e.g. inviting someone who already has a usable
 * password. The server sends them an access-granted notification email
 * instead, which is what that branch's copy reflects. `email` is optional
 * and, when provided, is woven into the headline copy.
 */
export function InviteLinkBox({
  link,
  email,
  onCopied,
  emailSent,
}: {
  link?: string | null;
  email?: string;
  onCopied?: () => void;
  emailSent?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopied?.();
  };

  if (!link) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {email ? `Membership added for ${email}` : "Membership added"}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
          This email already has an account, so we sent them a notification
          that access was granted instead of a password-setup link. They can
          sign in with their existing password.
        </Typography>
      </Alert>
    );
  }

  if (emailSent === false) {
    return (
      <Paper
        elevation={2}
        sx={{
          mt: 2,
          p: 2.5,
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {email ? `Email failed to send — account created for ${email}` : "Email failed to send"}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
            The account was created, but we could not email the recipient.
            Please copy the link below and share it with them directly.
          </Typography>
        </Alert>
        <Box
          sx={{
            bgcolor: "background.default",
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ fontFamily: "monospace", wordBreak: "break-all", color: "text.primary" }}
          >
            {link}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          onClick={handleCopy}
        >
          {copied ? "Copied!" : "Copy link"}
        </Button>
      </Paper>
    );
  }

  return (
    <Alert severity="success" sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {email ? `Account created for ${email}` : "Account created"}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
        {email
          ? `A password-setup email has been sent to ${email}.`
          : "A password-setup email has been sent."}
      </Typography>
    </Alert>
  );
}
